import React from 'react';
import {
  WiBarometer,
  WiHumidity,
  WiThermometerExterior,
  WiStrongWind,
  WiWindy,
  WiWindDeg,
  WiCloudy,
  WiUmbrella,
  WiRain,
  WiRaindrop,
  WiDaySunny,
  WiDayHaze,
  WiNA,
} from 'weather-icons-react';

// Custom icons (motorsport-specific only)
import { TrackTempIcon } from './FieldIcons';

// Icon props interface (compatible with weather-icons-react)
export interface IconProps {
  size?: number;
  color?: string;
  className?: string;
}

// Weather Icons wrapper components to match our interface
const BarometerIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiBarometer, { size, color, className });

const HumidityIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiHumidity, { size, color, className });

// Single thermometer icon for ALL temperature-related fields
const ThermometerIcon: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiThermometerExterior, { size, color, className });

const WindSpeedIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiStrongWind, { size, color, className });

const WindGustsIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiWindy, { size, color, className });

const WindDirectionIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiWindDeg, { size, color, className });

const CloudCoverIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiCloudy, { size, color, className });

const RainProbabilityIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiUmbrella, { size, color, className });

const PrecipitationIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiRain, { size, color, className });

const DewPointIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiRaindrop, { size, color, className });

const UVIndexIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiDaySunny, { size, color, className });

const VisibilityIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiDayHaze, { size, color, className });

const WeatherCodeIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiCloudy, { size, color, className });

const GenericFieldIconWi: React.FC<IconProps> = ({ size = 16, color = 'currentColor', className }) => 
  React.createElement(WiNA, { size, color, className });

// Map of db_name to icon component
// Includes both snake_case (forecast fields) and camelCase (observation fields) names
export const FIELD_ICONS: Record<string, React.FC<IconProps>> = {
  // Forecast fields (snake_case)
  rain_probability: RainProbabilityIconWi,
  precipitation_probability: RainProbabilityIconWi,
  humidity: HumidityIconWi,
  temperature: ThermometerIcon,
  temperature_apparent: ThermometerIcon,
  max_temp: ThermometerIcon,
  min_temp: ThermometerIcon,
  wind_speed: WindSpeedIconWi,
  min_wind_speed: WindSpeedIconWi,
  max_wind_speed: WindSpeedIconWi,
  wind_gust: WindGustsIconWi,
  wind_gusts: WindGustsIconWi,
  wind_direction: WindDirectionIconWi,
  surface_pressure: BarometerIcon,
  pressure_surface_level: BarometerIcon,
  sea_pressure: BarometerIcon,
  pressure_sea_level: BarometerIcon,
  cloud_cover: CloudCoverIconWi,
  heat_index: ThermometerIcon,
  track_temp: TrackTempIcon,
  track_temp_max: TrackTempIcon,
  track_temp_min: TrackTempIcon,
  dew_point: DewPointIconWi,
  visibility: VisibilityIconWi,
  uv_index: UVIndexIconWi,
  solar_ghi: UVIndexIconWi,
  weather_code: WeatherCodeIconWi,
  weather_code_am: WeatherCodeIconWi,
  weather_code_pm: WeatherCodeIconWi,
  precipitation_intensity: PrecipitationIconWi,
  
  // Observation fields (camelCase - Tomorrow.io API naming)
  dewPoint: DewPointIconWi,
  rainIntensity: PrecipitationIconWi,
  precipitationType: RainProbabilityIconWi,
  precipitationIntensity: PrecipitationIconWi,
  trackTemperature: TrackTempIcon,
  wetBulbGlobeTemperature: ThermometerIcon,
  heatIndex: ThermometerIcon,
  windSpeed: WindSpeedIconWi,
  windGust: WindGustsIconWi,
  windDirection: WindDirectionIconWi,
  pressureSurfaceLevel: BarometerIcon,
  pressureSeaLevel: BarometerIcon,
  lightIntensity: UVIndexIconWi,
};

// Helper to get icon component for a field
export const getFieldIconComponent = (dbName: string): React.FC<IconProps> => {
  return FIELD_ICONS[dbName] || GenericFieldIconWi;
};

// Helper to render icon for a field (returns React node)
export const getFieldIcon = (dbName: string, props?: IconProps): React.ReactNode => {
  const IconComponent = getFieldIconComponent(dbName);
  return React.createElement(IconComponent, props);
};
